import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { RefreshCw, Check, X, GitBranch, Sparkles, ShieldCheck } from "lucide-react";

const Eyebrow = ({ children }) => (
  <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[var(--e360-primary)] mb-2">{children}</div>
);
const inp = "ek-input w-full border border-zinc-300 rounded-md px-3 py-2 text-[15px]";
const sel = "ek-select w-full border border-zinc-300 rounded-md px-3 py-2 text-[15px] bg-white";

const statusColor = (s) => ({
  candidate: "bg-amber-100 text-amber-700", accepted: "bg-emerald-100 text-emerald-700",
  rejected: "bg-zinc-200 text-zinc-500", published: "bg-violet-100 text-[var(--e360-primary)]",
}[s] || "bg-zinc-100 text-zinc-600");

const kindLabel = (k) => ({ map_diff: "Map diff", note: "Note", section_override: "Section override" }[k] || k);

export default function LearningLoopPanel({ clients = [] }) {
  const [candidates, setCandidates] = useState([]);
  const [versions, setVersions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [capClient, setCapClient] = useState("");
  const [note, setNote] = useState({ stream_id: "", title: "", body: "" });

  const loadCandidates = useCallback(() => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    api.get(`/admin/enterprise360/learning/candidates${q}`)
      .then(({ data }) => setCandidates(data.items || [])).catch(() => {});
  }, [statusFilter]);

  const loadVersions = useCallback(() => {
    api.get("/admin/enterprise360/learning/versions")
      .then(({ data }) => setVersions(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const acceptedCount = candidates.filter((c) => c.status === "accepted").length;

  const capture = async () => {
    if (!capClient) { toast.error("Select a client engagement to capture from"); return; }
    setBusy(true);
    try {
      const body = {};
      if (note.title || note.body) {
        body.note = { stream_id: note.stream_id || null, title: note.title || null, body: note.body || null };
      }
      const { data } = await api.post(`/admin/enterprise360/clients/${capClient}/learning/capture`, body);
      toast.success(`Captured ${data.captured} anonymised learning(s)`);
      setNote({ stream_id: "", title: "", body: "" });
      loadCandidates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Capture failed"); }
    finally { setBusy(false); }
  };

  const curate = async (id, status) => {
    try {
      await api.put(`/admin/enterprise360/learning/candidates/${id}`, { status });
      toast.success(`Marked ${status}`);
      loadCandidates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const publish = async () => {
    if (acceptedCount === 0) { toast.error("No accepted learnings to publish"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/enterprise360/learning/publish", {});
      toast.success(`Published library ${data.version} (${data.changelog?.length || 0} learnings)`);
      loadCandidates(); loadVersions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed (owner-only)"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="learning-loop-panel" className="space-y-8">
      <div>
        <Eyebrow><Sparkles size={13} className="inline mr-1" /> Governed learning loop</Eyebrow>
        <h2 className="text-2xl font-semibold text-black">Capture · curate · publish</h2>
        <p className="text-sm text-zinc-500 mt-1 flex items-center gap-1">
          <ShieldCheck size={14} className="text-emerald-600" />
          Learnings are anonymised on capture — no client name or figures leave the engagement. Publishing creates a versioned authoring backlog; it never edits the deterministic bricks.
        </p>
      </div>

      {/* Capture */}
      <div className="border border-zinc-200 rounded-lg p-4 space-y-3">
        <Eyebrow>Capture from a finalised engagement</Eyebrow>
        <div className="grid sm:grid-cols-3 gap-3">
          <select data-testid="ll-capture-client" className={sel} value={capClient} onChange={(e) => setCapClient(e.target.value)}>
            <option value="">Select client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
          </select>
          <input data-testid="ll-note-stream" className={inp} placeholder="stream (e.g. source_to_pay)"
            value={note.stream_id} onChange={(e) => setNote({ ...note, stream_id: e.target.value })} />
          <input data-testid="ll-note-title" className={inp} placeholder="learning title (optional)"
            value={note.title} onChange={(e) => setNote({ ...note, title: e.target.value })} />
        </div>
        <textarea data-testid="ll-note-body" className={`${inp} min-h-[70px]`} placeholder="What did the consultant change and why? (anonymised on save)"
          value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })} />
        <div className="flex items-center gap-3">
          <button data-testid="ll-capture-btn" onClick={capture} disabled={busy}
            className="bg-[var(--e360-primary)] text-white px-5 py-2.5 text-[12px] tracking-[0.16em] uppercase disabled:opacity-50">
            {busy ? "Capturing…" : "Capture learnings"}
          </button>
          <span className="text-xs text-zinc-400">Map edits (generated-vs-delivered) are captured automatically alongside your note.</span>
        </div>
      </div>

      {/* Candidates */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Eyebrow>Learning candidates ({candidates.length})</Eyebrow>
          <div className="flex items-center gap-1">
            {["", "candidate", "accepted", "rejected", "published"].map((s) => (
              <button key={s || "all"} data-testid={`ll-filter-${s || "all"}`} onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 text-[11px] uppercase tracking-wider border ${statusFilter === s ? "border-[var(--e360-primary)] text-black" : "border-zinc-200 text-zinc-400"}`}>
                {s || "all"}
              </button>
            ))}
            <button onClick={loadCandidates} className="text-zinc-400 hover:text-black ml-1"><RefreshCw size={15} /></button>
          </div>
        </div>
        {candidates.length === 0 ? (
          <p className="text-sm text-zinc-500">No learnings captured yet. Capture from a finalised client engagement above.</p>
        ) : (
          <div className="space-y-2">
            {candidates.map((c) => (
              <div key={c.id} data-testid={`ll-candidate-${c.id}`} className="border border-zinc-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-600 rounded px-2 py-0.5">{kindLabel(c.kind)}</span>
                      <span className="text-xs text-zinc-400">{c.archetype_id}{c.stream_id ? ` · ${c.stream_id}` : ""}{c.variant ? ` · ${c.variant}` : ""}</span>
                      <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-0.5 ${statusColor(c.status)}`}>{c.status}{c.version ? ` ${c.version}` : ""}</span>
                    </div>
                    <div className="font-medium text-black mt-1">{c.title}</div>
                    <p className="text-sm text-zinc-600 mt-0.5">{c.pattern}</p>
                  </div>
                  {c.status === "candidate" && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button data-testid={`ll-accept-${c.id}`} onClick={() => curate(c.id, "accepted")}
                        title="Accept" className="p-1.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"><Check size={15} /></button>
                      <button data-testid={`ll-reject-${c.id}`} onClick={() => curate(c.id, "rejected")}
                        title="Reject" className="p-1.5 rounded bg-rose-50 text-rose-600 hover:bg-rose-100"><X size={15} /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Publish + versions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Eyebrow><GitBranch size={13} className="inline mr-1" /> Published library versions ({versions.length})</Eyebrow>
          <button data-testid="ll-publish-btn" onClick={publish} disabled={busy || acceptedCount === 0}
            className="bg-[#0B0B0C] text-white px-4 py-2 text-[12px] tracking-[0.16em] uppercase rounded disabled:opacity-40">
            Publish {acceptedCount} accepted →
          </button>
        </div>
        {versions.length === 0 ? (
          <p className="text-sm text-zinc-500">No library versions published yet. Accept learnings, then publish (owner-only).</p>
        ) : (
          <div className="space-y-2">
            {versions.map((v) => (
              <details key={v.id} data-testid={`ll-version-${v.id}`} className="border border-zinc-200 rounded-lg p-3">
                <summary className="cursor-pointer font-medium text-black">
                  {v.version} · {v.label} <span className="text-xs text-zinc-400">· {(v.changelog || []).length} learnings · {new Date(v.published_at).toLocaleDateString()}</span>
                </summary>
                {v.notes && <p className="text-sm text-zinc-500 mt-2 italic">{v.notes}</p>}
                <ul className="mt-2 space-y-1">
                  {(v.changelog || []).map((cl) => (
                    <li key={cl.id} className="text-sm text-zinc-600">
                      <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-500 rounded px-1.5 py-0.5 mr-2">{kindLabel(cl.kind)}</span>
                      <span className="text-black">{cl.title}</span> — {cl.pattern}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
